import {useEffect, useState} from "react";
import {MapPin} from "lucide-react";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {cn} from "@/lib/utils";
import {
  FORM_SHEET_ALERT_CLASS,
  FORM_SHEET_ALERT_DESCRIPTION_CLASS,
  FORM_SHEET_ALERT_TITLE_CLASS,
} from "@/src/modules/core/lib/form-sheet-alert";
import useGeolocation, {
  useLocationCaptureBlocked,
} from "@/src/modules/core/hooks/use-geolocation";
import {isNativeMobileGeolocationPlatform} from "@/src/modules/core/lib/geolocation-platform";

/** Grace before nagging about a missing fix — avoids a flash when GPS locks quickly. */
const NO_FIX_NOTICE_DELAY_MS = 6000;

type CoordinatesRequiredGateProps = {
  className?: string;
};

/**
 * In-sheet location notice. Mount it unconditionally inside a coordinate-required
 * form — it self-decides what (if anything) to show. Submit stays disabled via
 * `useCoordinatesGate` while permission is blocked; this is the user-facing copy.
 *
 *   - permission blocked (denied)        → destructive "Location is turned off"
 *   - granted but no fix after a grace   → warning "Waiting for your location"
 *     (covers iOS "Location Services off" → POSITION_UNAVAILABLE, which is not a denial)
 *   - fix available                      → null
 */
export function CoordinatesRequiredGate({className}: CoordinatesRequiredGateProps) {
  const locationCaptureBlocked = useLocationCaptureBlocked();
  const {fix} = useGeolocation();
  const [graceElapsed, setGraceElapsed] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => setGraceElapsed(true), NO_FIX_NOTICE_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  // Desktop falls back to property coordinates and never blocks submit here, so it
  // shows no location warnings — only mobile (device GPS mandatory) does.
  if (!isNativeMobileGeolocationPlatform()) {
    return null;
  }

  if (locationCaptureBlocked) {
    return (
      <Alert variant="destructive" className={cn(FORM_SHEET_ALERT_CLASS, className)}>
        <MapPin className="size-4" />
        <AlertTitle className={FORM_SHEET_ALERT_TITLE_CLASS}>Location is turned off</AlertTitle>
        <AlertDescription className={FORM_SHEET_ALERT_DESCRIPTION_CLASS}>
          Enable location in your browser or device settings. This form unlocks automatically when
          location is available.
        </AlertDescription>
      </Alert>
    );
  }

  if (!graceElapsed || fix) {
    return null;
  }

  return (
    <Alert className={cn(FORM_SHEET_ALERT_CLASS, className)}>
      <MapPin className="size-4" />
      <AlertTitle className={FORM_SHEET_ALERT_TITLE_CLASS}>Waiting for your location</AlertTitle>
      <AlertDescription className={FORM_SHEET_ALERT_DESCRIPTION_CLASS}>
        Turn on GPS / location so this form can save — make sure you have signal. We'll keep trying
        in the background.
      </AlertDescription>
    </Alert>
  );
}
